import { PRIORITY_COLOUR } from '@tradeoutlet/shared';
import clsx from 'clsx';
import { AlertCircle, ArrowDown, ArrowUp, Minus, ChevronsUp } from 'lucide-react';
import type { ProjectStatus, TaskPriority, TaskSummary } from '@tradeoutlet/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { qk, queryClient } from '@/lib/queryClient';
import { useMembers } from '@/lib/hooks';
import { Avatar } from '@/components/ui';

export const PRIORITIES: { id: TaskPriority; label: string }[] = [
  { id: 'urgent', label: 'Urgent' }, { id: 'high', label: 'High' }, { id: 'medium', label: 'Medium' }, { id: 'low', label: 'Low' }, { id: 'none', label: 'No priority' },
];

export function PriorityIcon({ priority, className }: { priority: TaskPriority; className?: string }) {
  const cls = clsx('h-4 w-4 shrink-0', className);
  switch (priority) {
    case 'urgent': return <span title="Urgent"><AlertCircle className={cls} style={{ color: PRIORITY_COLOUR.urgent }} /></span>;
    case 'high': return <span title="High"><ChevronsUp className={cls} style={{ color: PRIORITY_COLOUR.high }} /></span>;
    case 'medium': return <span title="Medium"><ArrowUp className={cls} style={{ color: PRIORITY_COLOUR.medium }} /></span>;
    case 'low': return <span title="Low"><ArrowDown className={cls} style={{ color: PRIORITY_COLOUR.low }} /></span>;
    default: return <span title="No priority"><Minus className={clsx(cls, 'text-slate-300')} /></span>;
  }
}

export function StatusDot({ status }: { status?: Pick<ProjectStatus, 'color' | 'category'> }) {
  return <span className={clsx('inline-block h-2.5 w-2.5 shrink-0 rounded-full', status?.category === 'done' && 'ring-2 ring-offset-1 ring-emerald-300')} style={{ background: status?.color ?? '#94a3b8' }} />;
}

export function AssigneeStack({ ids, max = 3, size = 22 }: { ids: string[]; max?: number; size?: number }) {
  const { byId } = useMembers();
  if (!ids.length) return null;
  return (
    <span className="flex -space-x-1.5">
      {ids.slice(0, max).map(id => <span key={id} className="rounded-full ring-2 ring-white dark:ring-slate-900"><Avatar size={size} name={byId.get(id)?.display_name} id={id} /></span>)}
      {ids.length > max && <span className="flex items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold ring-2 ring-white dark:bg-slate-700 dark:ring-slate-900" style={{ width: size, height: size }}>+{ids.length - max}</span>}
    </span>
  );
}

export function useProjectData(projectId: string, includeCompleted = true) {
  const project = useQuery({ queryKey: qk.project(projectId), queryFn: () => api.project(projectId), enabled: Boolean(projectId) });
  const tasks = useQuery({
    queryKey: [...qk.tasks(projectId), includeCompleted],
    queryFn: () => api.tasks(projectId, { include_completed: includeCompleted }),
    enabled: Boolean(projectId),
  });
  return { project, tasks };
}

/** Patch a task in every cached task list for the project (optimistic updates / realtime). */
export function patchTaskInCache(projectId: string, task: Partial<TaskSummary> & { id: string }, remove = false) {
  queryClient.setQueriesData<{ tasks: TaskSummary[] }>({ queryKey: qk.tasks(projectId) }, old => {
    if (!old) return old;
    const exists = old.tasks.some(t => t.id === task.id);
    if (remove) return { tasks: old.tasks.filter(t => t.id !== task.id) };
    if (!exists) return 'title' in task && 'status_id' in task ? { tasks: [...old.tasks, task as TaskSummary] } : old;
    return { tasks: old.tasks.map(t => (t.id === task.id ? { ...t, ...task } : t)) };
  });
}

export { positionBetween } from './positions';
